import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import type { User, UserInsert, UserUpdate } from '../../types/supabase'

/**
 * User Management Hooks
 * 
 * MSP Platform Architecture:
 * - ADMIN: Help Desk 411 MSP staff (created via internal tools, NOT public registration)
 * - MANAGER: Client companies who pay for services (public registration default)
 * - EMPLOYEE: Client's staff members (invited by their manager)
 * 
 * Public registration ALWAYS creates 'manager' role users who own their company.
 * Admins are created separately by Help Desk 411 staff.
 * Employees are invited by managers through the team setup flow.
 */

// Get current user
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      // If user doesn't exist in users table, create a basic record
      // MSP Architecture: Public registration creates 'manager' (client companies)
      // Admins are Help Desk 411 staff (created separately, not via public registration)
      // Employees are invited by managers with pre-assigned role
      if (error || !data) {
        const newUserData = {
          id: user.id,
          email: user.email || '',
          role: 'manager' as const, // Public registration = client/manager
          full_name: user.user_metadata?.full_name || user.email || '',
          is_active: true,
          onboarding_completed: false
        }
        
        // Try to create the user record
        const { data: createdUser, error: createError } = await supabase
          .from('users')
          .insert(newUserData)
          .select()
          .single()
        
        if (createError) {
          // If creation fails, return the basic user data
          return newUserData as User
        }
        
        return createdUser as User
      }
      
      return data as User
    },
    enabled: true
  })
}

// Get all users in the company
export const useCompanyUsers = (companyId?: string) => {
  return useQuery({
    queryKey: ['companyUsers', companyId ?? 'self'],
    queryFn: async () => {
      let targetCompanyId = companyId

      if (!targetCompanyId) {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        targetCompanyId = user.user_metadata?.company_id || undefined

        if (!targetCompanyId) {
          const { data: userRecord, error } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', user.id)
            .maybeSingle()

          if (error) throw error
          targetCompanyId = userRecord?.company_id || undefined
        }
      }

      if (!targetCompanyId) {
        return [] as User[]
      }

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('company_id', targetCompanyId)

      if (error) throw error
      return data as User[]
    },
    enabled: true
  })
}

// Create a new user
export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: UserInsert) => {
      const { data, error } = await supabase
        .from('users')
        .insert(userData)
        .select()
        .single()

      if (error) throw error
      return data as User
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] })
    }
  })
}

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { id: string } & UserUpdate) => {
      // First check if user exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('id', id)
        .maybeSingle()

      if (!existingUser) {
        // If user doesn't exist, create them first
        // Default to 'manager' for public registration (client companies)
        const { data: { user: authUser } } = await supabase.auth.getUser()
        if (authUser && authUser.id === id) {
          const newUserData = {
            id: authUser.id,
            email: authUser.email || '',
            role: 'manager' as const, // Public registration = client/manager
            full_name: authUser.user_metadata?.full_name || authUser.email || '',
            is_active: true,
            onboarding_completed: false,
            ...updateData
          }
          
          const { data, error } = await supabase
            .from('users')
            .insert(newUserData)
            .select()
            .single()

          if (error) throw error
          return data as User
        } else {
          throw new Error('User not found and cannot be created')
        }
      }

      // Update existing user
      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as User
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] })
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['onboardingStatus'] })
    }
  })
}

// Delete user (admin only)
export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      if (error) throw error
      return userId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] })
      queryClient.invalidateQueries({ queryKey: ['companiesList'] })
    }
  })
}
